import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getInventoryHistory, getKpuState } from 'requests/history.request';
import { IHistory, IKpu, IObject, ITankState } from 'interfaces/applicant.interface';
import Pagination from 'components/pagination/pagination.component';
import Loader from 'components/loader/loader.component';
import Icon from 'components/icon/icon.component';
import Barrel from 'components/barrel/barrel.component';
import Select from 'components/select/select.component';
import './history.style.scss';

const History = () => {
  const itemsPerPage = 10;
  const columns: string[] = [
    'name',
    'prodLevel',
    'prodDensity',
    'temp',
    'volume',
    'mass',
    'dateTime',
    'sentToQoldau',
    'errorMessageWhenSend',
    'errorMessageCheckStatus',
  ];
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [dropdownObjects, setDropdownObjects] = useState<Record<string, any>[]>([]);
  const [objectId, setObjectId] = useState<number>();
  const [objects, setObjects] = useState<IObject[]>([]);
  const [page, setPage] = useState<number>(0);
  const [history, setHistory] = useState<IHistory[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const handleGetKpuState = () => {
    getKpuState()
      .then((res) => {
        setObjects(res.objects);
        const filteredObjects = res.objects.filter((obj) =>
          obj.kpus?.some((kpu) => kpu?.tanks?.some((tank: ITankState) => tank.id)),
        );
        setObjectId(filteredObjects[0].id);
        setDropdownObjects(filteredObjects);
      })
      .catch(() => null);
  };

  const handleGetInventoryHistory = () => {
    const params: Record<string, number> = {
      page,
      size: itemsPerPage,
    };
    // Send cached total on subsequent pages so backend skips the expensive COUNT
    if (totalItems > 0) {
      params.totalElements = totalItems;
    }
    getInventoryHistory(params)
      .then((res) => {
        setHistory(res.content);
        setTotalItems(res.totalElements);
      })
      .catch(() => null);
  };

  useEffect(() => {
    handleGetKpuState();
    const interval = setInterval(() => {
      handleGetKpuState();
    }, 15 * 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    handleGetInventoryHistory();
  }, [page]);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const getNames = (kpu: IKpu, tank: ITankState) => {
    return [kpu.name, tank.name].filter((name) => name !== undefined && name !== '') as string[];
  };

  const handleChangeObject = ({ target }: React.ChangeEvent<HTMLSelectElement>) => {
    setObjectId(+target.value);
  };

  const getTanks = useMemo(
    () =>
      objects
        .filter((obj) => obj.id === objectId)
        .map(
          (object) =>
            object.kpus &&
            object.kpus.length > 0 &&
            object.kpus.map(
              (kpu) =>
                kpu.tanks &&
                kpu.tanks.length > 0 &&
                kpu.tanks.map((tank: ITankState, index) => (
                  <div className="history__tank__item" key={`tank_${index}`}>
                    <div className="history__tank__name">
                      {getNames(kpu, tank).map((name, i) => (
                        <React.Fragment key={`tank_name_${i}`}>
                          <span>{name}</span>
                          <Icon name="ArrowRight" size={14} />
                        </React.Fragment>
                      ))}
                    </div>
                    <Barrel tank={tank} />
                    <div className="history__tank__data">
                      <span>
                        <strong>{t('history.mass')}</strong>
                        {': '}
                        {tank.mass}{' '}
                        {tank.massUnitType && t(`enum.massUnitShort.${tank.massUnitType}`)}
                      </span>
                      <span>
                        <strong>{t('history.prodLevel')}</strong>
                        {': '}
                        {tank.prodLevel}{' '}
                        {tank.levelUnitType && t(`enum.levelUnitShort.${tank.levelUnitType}`)}
                      </span>
                      <span>
                        <strong>{t('history.prodDensity')}</strong>
                        {': '}
                        {tank.prodDensity}
                      </span>
                      <span>
                        <strong>{t('history.temp')}</strong>
                        {': '}
                        {tank.temp}
                      </span>
                    </div>
                  </div>
                )),
            ),
        ),
    [objects, objectId],
  );

  const getTableValue = (item: IHistory, column: keyof IHistory) => {
    switch (column) {
      case 'sentToQoldau':
        return t(`enum.yesNo.${item.sentToQoldau ? 'yes' : 'no'}`);
      case 'errorMessageWhenSend':
        return item.errorMessageWhenSend ?? '';
      case 'errorMessageCheckStatus':
        return item.errorMessageCheckStatus ?? '';
      default:
        return item[column];
    }
  };

  return (
    <Loader loading={loading}>
      <div className="wrapper">
        <h1 className="title">Текущий статус</h1>
        {dropdownObjects.length > 1 && (
          <div className="history__selectWrapper">
            <Select
              name={'objectId'}
              options={dropdownObjects}
              value={objectId}
              onChange={handleChangeObject}
              labelKey={'objectName'}
              label={'Объект'}
            />
          </div>
        )}
        <div className="history__tank__list">{getTanks}</div>
        <h1 className="title">История</h1>
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{t(`history.${column}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {history.map((item, index) => (
              <tr key={`tableRow_${index}`}>
                {columns.map((column) => (
                  <td key={`${column}_${index}`}>
                    {getTableValue(item, column as keyof IHistory)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          currentPage={page}
          onPageChange={handlePageChange}
          centered={true}
        />
      </div>
    </Loader>
  );
};

export default History;
